import React, { useMemo, useState } from "react";
import { PageShell } from "../components/Layout";
import api from "../lib/api";
import { Sparkle, ArrowRight, Coin, Briefcase, Heart, Crown, Eye, House } from "@phosphor-icons/react";
import { toast } from "sonner";
import ToolAstrologerCta from "../components/ToolAstrologerCta";
import KundaliChart from "../components/KundaliChart";
import usePrefillBirthDetails from "../hooks/usePrefillBirthDetails";

const HOUSE_ICON = { 1: Eye, 2: Coin, 10: Briefcase, 11: Crown };
const HOUSE_TINT = { 1: "amber", 2: "emerald", 10: "indigo", 11: "rose" };

export default function SaptavimshamshaPage() {
  const [form, setForm] = useState({ date: "", time: "", place: "", timezone: "Asia/Kolkata" });
  const [placeQuery, setPlaceQuery] = useState("");
  const [suggestions, setSuggestions] = useState([]);
  const [loading, setLoading] = useState(false);
  const [data, setData] = useState(null);
  usePrefillBirthDetails(setForm, setPlaceQuery);

  const submit = async (e) => {
    e?.preventDefault();
    const place = form.place || placeQuery;
    if (!form.date || !form.time || !place) {
      toast.error("Please fill date, time and place");
      return;
    }
    setLoading(true);
    try {
      const r = await api.post("/saptavimshamsha/compute", { ...form, place });
      setData(r.data);
      setTimeout(() => document.querySelector("[data-testid='d27-result']")?.scrollIntoView({ behavior: "smooth" }), 100);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed to compute D27");
    } finally {
      setLoading(false);
    }
  };

  const d27Kundali = useMemo(() => {
    if (!data?.d27_lagna?.rashi || !data?.d27_planets) return null;
    return {
      lagna: { rashi: data.d27_lagna.rashi },
      planets: Object.fromEntries(
        Object.entries(data.d27_planets).map(([name, p]) => [
          name,
          { rashi: p.rashi, retrograde: !!p.retrograde },
        ])
      ),
    };
  }, [data]);

  return (
    <PageShell>
      <div className="max-w-6xl mx-auto px-5 sm:px-6 lg:px-10 pt-10 pb-20">
        <header className="mb-10">
          <div className="label-eyebrow text-muted">D27 · Saptavimshamsha (Bhamsha)</div>
          <h1 className="font-serif-display text-4xl sm:text-5xl lg:text-6xl mt-3 font-light leading-tight">
            Your hidden strength, decoded
          </h1>
          <p className="text-muted text-sm sm:text-base mt-4 font-light max-w-2xl">
            The Saptavimshamsha is Parashara's divisional chart of <b>hidden strength</b> — who you become when pressure peels back the surface, where wealth is truly held, and which combinations carry rare yogas no other chart will show.
          </p>
        </header>

        <form onSubmit={submit} className="rounded-2xl p-6 sm:p-7 bg-[#FAF4E8] border border-soft space-y-5 max-w-2xl" data-testid="d27-form">
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <label className="space-y-1">
              <span className="text-xs text-muted">Date of birth</span>
              <input type="date" value={form.date} onChange={(e) => setForm({ ...form, date: e.target.value })} className="w-full px-3 py-2.5 rounded-md border border-soft bg-white" data-testid="d27-date" required />
            </label>
            <label className="space-y-1">
              <span className="text-xs text-muted">Time of birth</span>
              <input type="time" value={form.time} onChange={(e) => setForm({ ...form, time: e.target.value })} className="w-full px-3 py-2.5 rounded-md border border-soft bg-white" data-testid="d27-time" required />
            </label>
          </div>
          <div className="relative">
            <label className="space-y-1 block">
              <span className="text-xs text-muted">Place of birth</span>
              <input
                type="text" value={placeQuery}
                onChange={async (e) => {
                  const q = e.target.value;
                  setPlaceQuery(q);
                  if (q.trim().length >= 2) {
                    try {
                      const { data } = await api.get("/places/search", { params: { q: q.trim() } });
                      setSuggestions(data || []);
                    } catch { /* ignore */ }
                  } else setSuggestions([]);
                }}
                placeholder="Start typing — Mumbai, Delhi, London..."
                className="w-full px-3 py-2.5 rounded-md border border-soft bg-white"
                data-testid="d27-place"
              />
            </label>
            {suggestions.length > 0 && (
              <div className="absolute top-full left-0 right-0 mt-1 max-h-60 overflow-y-auto card-surface rounded-md border border-soft z-20 shadow-lg">
                {suggestions.map((s, i) => (
                  <button type="button" key={i}
                    onClick={() => {
                      const label = s.name || s.label;
                      setForm({ ...form, place: label, timezone: s.timezone || "Asia/Kolkata" });
                      setPlaceQuery(label); setSuggestions([]);
                    }}
                    className="w-full text-left px-3 py-2 hover:bg-amber-50 text-sm border-b border-soft last:border-0"
                    data-testid={`d27-place-option-${i}`}
                  >{s.name || s.label}</button>
                ))}
              </div>
            )}
          </div>
          <button type="submit" disabled={loading} className="btn-primary rounded-full px-7 py-3 font-medium disabled:opacity-50" data-testid="d27-submit">
            {loading ? "Reading your hidden strengths…" : <>Reveal my D27 <ArrowRight size={16} className="inline ml-1" /></>}
          </button>
        </form>

        {data && (
          <div className="mt-12 space-y-10" data-testid="d27-result">
            {/* === D27 CHART (visual first) === */}
            {d27Kundali && (
              <section className="rounded-2xl overflow-hidden border border-amber-200 bg-gradient-to-br from-[#FFF8E7] via-[#FFF1D6] to-[#FFE7C2]" data-testid="d27-chart-section">
                <div className="p-6 sm:p-8 grid grid-cols-1 md:grid-cols-[auto,1fr] gap-7 items-center">
                  <div className="flex justify-center md:justify-start">
                    <div className="p-3 rounded-xl bg-white shadow-sm border border-amber-200">
                      <KundaliChart kundali={d27Kundali} size={320} />
                    </div>
                  </div>
                  <div>
                    <div className="label-eyebrow text-[#7C2D12] flex items-center gap-2">
                      <Sparkle size={14} weight="duotone" /> Your D27 · Saptavimshamsha chart
                    </div>
                    <h2 className="font-serif-display text-2xl sm:text-3xl mt-3 font-light leading-tight text-[#3F2D1B]">
                      Lagna in {data.d27_lagna.rashi} · ruled by {data.d27_lagna.rashi_lord}
                    </h2>
                    <p className="text-sm text-muted mt-3 font-light leading-snug">
                      This is your hidden-strength chart — the karmic blueprint of who you become under pressure. Read the chart first, then scroll down for the layered interpretation.
                    </p>
                    <div className="mt-5 grid grid-cols-2 sm:grid-cols-3 gap-3 text-xs">
                      {Object.entries(data.d27_planets).map(([name, p]) => (
                        <div key={name} className="px-3 py-2 rounded-md bg-white/70 border border-amber-200" data-testid={`d27-legend-${name}`}>
                          <div className="font-medium text-[#3F2D1B]">{name}{p.retrograde && <span className="text-rose-700 ml-1">(R)</span>}</div>
                          <div className="text-muted">H{p.house} · {p.rashi}</div>
                        </div>
                      ))}
                    </div>
                    <div className="mt-4 text-[11px] text-muted italic">Lg = D27 Lagna. Numbers in cells are the rashi index (1=Aries → 12=Pisces). Planet codes: Su, Mo, Ma, Me, Ju, Ve, Sa, Ra, Ke.</div>
                  </div>
                </div>
              </section>
            )}

            {/* === D27 LAGNA — HIDDEN PERSONALITY === */}
            <section className="rounded-2xl overflow-hidden border border-amber-200">
              <div className="p-7 sm:p-9 bg-gradient-to-br from-amber-50 via-amber-100 to-amber-50">
                <div className="flex items-center gap-2 label-eyebrow text-[#7C2D12]">
                  <Sparkle size={14} weight="duotone" /> D27 Lagna · the hidden self
                </div>
                <h2 className="font-serif-display text-3xl sm:text-4xl lg:text-5xl mt-3 font-light leading-tight" data-testid="d27-profile-name">
                  {data.d27_lagna.profile_name}
                </h2>
                <div className="text-xs text-muted mt-2 font-medium">
                  D27 Lagna · {data.d27_lagna.rashi} · ruled by {data.d27_lagna.rashi_lord}
                </div>
                <p className="font-serif-display text-xl sm:text-2xl mt-5 font-light leading-snug text-[#3F2D1B]">
                  {data.d27_lagna.profile}
                </p>
              </div>
              {data.d27_lagna.deity && (
                <div className="p-6 sm:p-7 bg-[#FFF6E0] border-t border-amber-200" data-testid="d27-deity-block">
                  <div className="label-eyebrow text-amber-800">Nakshatra deity overlay · {data.d27_lagna.nakshatra}</div>
                  <div className="font-serif-display text-2xl mt-3 font-light">{data.d27_lagna.deity}</div>
                  <p className="text-sm text-muted mt-2 font-light leading-snug">{data.d27_lagna.deity_note}</p>
                </div>
              )}
            </section>

            {/* === WEALTH BAND === */}
            <section className="rounded-2xl p-6 sm:p-7 bg-gradient-to-br from-emerald-50 to-emerald-100 border border-emerald-200" data-testid="wealth-band">
              <div className="label-eyebrow text-emerald-900">Wealth potential band</div>
              <h3 className="font-serif-display text-3xl sm:text-4xl mt-2 font-light text-emerald-950">{data.wealth_band.label}</h3>
              <p className="text-sm text-emerald-950 mt-4 font-light leading-snug">{data.wealth_band.summary}</p>
            </section>

            {/* === BUSINESS TITAN SIGNATURE (author's research) === */}
            {data.business_titan && (
              <section className="rounded-2xl overflow-hidden border border-amber-300" data-testid="business-titan">
                <div className="p-6 sm:p-8 bg-gradient-to-br from-[#1A1410] via-[#2B1F12] to-[#3D2B1A] text-amber-50">
                  <div className="label-eyebrow text-amber-300 flex items-center gap-2">
                    <Crown size={14} weight="duotone" /> Business titan signature · research-backed
                  </div>
                  <h3 className="font-serif-display text-3xl sm:text-4xl mt-3 font-light leading-tight">
                    {data.business_titan.verdict_label}
                  </h3>
                  <p className="text-sm mt-4 font-light leading-snug text-amber-100">{data.business_titan.verdict_summary}</p>
                  <div className="mt-5 grid grid-cols-2 sm:grid-cols-4 gap-3 text-xs">
                    <div className="px-3 py-2 rounded-md bg-white/10 border border-amber-200/30">
                      <div className="text-amber-300/80 text-[10px] uppercase tracking-wider">D27 Lagna</div>
                      <div className="font-medium mt-0.5">{data.business_titan.d27_lagna}</div>
                    </div>
                    <div className="px-3 py-2 rounded-md bg-white/10 border border-amber-200/30">
                      <div className="text-amber-300/80 text-[10px] uppercase tracking-wider">D27 Moon</div>
                      <div className="font-medium mt-0.5">{data.business_titan.d27_moon_rashi}</div>
                    </div>
                    <div className="px-3 py-2 rounded-md bg-white/10 border border-amber-200/30">
                      <div className="text-amber-300/80 text-[10px] uppercase tracking-wider">10th rashi</div>
                      <div className="font-medium mt-0.5">{data.business_titan.d27_10h_rashi}</div>
                    </div>
                    <div className="px-3 py-2 rounded-md bg-white/10 border border-amber-200/30">
                      <div className="text-amber-300/80 text-[10px] uppercase tracking-wider">10th lord</div>
                      <div className="font-medium mt-0.5">{data.business_titan.d27_10th_lord} · {data.business_titan.d27_10th_lord_dignity}</div>
                    </div>
                  </div>
                </div>
                <div className="p-6 sm:p-7 bg-[#FFF8E7] divide-y divide-amber-200">
                  {data.business_titan.signals.map((s, i) => {
                    const verdictTint = {
                      "THE SECRET": "bg-amber-500 text-white",
                      "peak": "bg-amber-500 text-white",
                      "strong": "bg-emerald-200 text-emerald-900",
                      "redeemed": "bg-indigo-200 text-indigo-900",
                      "neutral": "bg-stone-200 text-stone-800",
                      "watch": "bg-rose-200 text-rose-900",
                    }[s.verdict] || "bg-stone-200 text-stone-800";
                    return (
                      <div key={i} className="py-4 first:pt-0 last:pb-0" data-testid={`titan-signal-${i}`}>
                        <div className="flex items-center gap-2 flex-wrap">
                          <span className="text-[10px] text-muted font-bold tracking-wider">{s.axis}</span>
                          <span className={`text-[9px] font-bold tracking-wider px-1.5 py-0.5 rounded ${verdictTint}`}>{s.verdict.toUpperCase()}</span>
                        </div>
                        <h4 className="font-serif-display text-lg sm:text-xl mt-2 font-light text-[#3F2D1B]">{s.headline}</h4>
                        <p className="text-sm mt-2 font-light leading-snug text-[#3F2D1B]">{s.detail}</p>
                      </div>
                    );
                  })}
                </div>
              </section>
            )}

            {/* === MARKETER SIGNATURE === */}
            {data.marketer_signature && (
              <section className="rounded-2xl p-6 sm:p-7 bg-gradient-to-br from-sky-50 to-cyan-50 border border-sky-200" data-testid="marketer-signature">
                <div className="label-eyebrow text-sky-900">Marketer / street-smart sales signature</div>
                <h3 className="font-serif-display text-2xl sm:text-3xl mt-2 font-light text-sky-950">{data.marketer_signature.headline}</h3>
                <p className="text-xs text-muted mt-2 italic">Steve-Jobs-class pattern: Gemini D27 Lagna · Mercury in Aquarius · Mars in Virgo.</p>
                <div className="mt-4 space-y-3">
                  {data.marketer_signature.flags.map((f, i) => (
                    <div key={i} className="rounded-xl p-4 bg-white/70 border border-sky-200" data-testid={`marketer-flag-${i}`}>
                      <div className="font-medium text-sky-950">{f.headline}</div>
                      <p className="text-xs mt-2 font-light leading-snug text-sky-900">{f.detail}</p>
                    </div>
                  ))}
                </div>
              </section>
            )}

            {/* === STRUGGLE FLAG === */}
            {data.struggle_flag && (
              <section className="rounded-2xl p-6 sm:p-7 bg-gradient-to-br from-rose-50 to-orange-50 border border-rose-300" data-testid="struggle-flag">
                <div className="label-eyebrow text-rose-900">⚠ Karmic struggle flag</div>
                <h3 className="font-serif-display text-2xl sm:text-3xl mt-2 font-light text-rose-950">{data.struggle_flag.headline}</h3>
                <p className="text-sm mt-3 font-light leading-snug text-rose-950">{data.struggle_flag.summary}</p>
                <div className="mt-4 space-y-3">
                  {data.struggle_flag.issues.map((it, i) => (
                    <div key={i} className="rounded-xl p-4 bg-white/70 border border-rose-200" data-testid={`struggle-issue-${i}`}>
                      <div className="font-medium text-rose-950">{it.role} · {it.lord}</div>
                      <p className="text-xs mt-2 font-light leading-snug text-rose-900">{it.note}</p>
                    </div>
                  ))}
                </div>
              </section>
            )}

            {/* === COMPANY-STARTING POINTER === */}
            {data.company_pointer && (
              <section className="rounded-2xl p-6 sm:p-7 bg-gradient-to-br from-violet-50 to-fuchsia-50 border border-violet-200" data-testid="company-pointer">
                <div className="label-eyebrow text-violet-900">Best dasha to start a company</div>
                <h3 className="font-serif-display text-2xl sm:text-3xl mt-2 font-light text-violet-950">{data.company_pointer.headline}</h3>
                <p className="text-sm mt-3 font-light leading-snug text-violet-950">{data.company_pointer.detail}</p>
              </section>
            )}

            {/* === POWER PLACEMENTS (exalted / own / neecha bhanga / parivartana) === */}
            {data.power_placements && data.power_placements.length > 0 && (
              <section className="space-y-4" data-testid="power-placements">
                <div className="label-eyebrow text-muted">Power placements in your D27 · dignity-based insights</div>
                <p className="text-xs text-muted -mt-2 font-light max-w-2xl">
                  These are planets sitting in <b>exaltation</b>, <b>own sign</b>, with <b>neecha bhanga</b> (cancelled debility), or locked in a <b>parivartana</b> exchange — each carries an outsized impact in the house it sits in.
                </p>
                <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
                  {data.power_placements.map((pp, i) => {
                    const tintMap = {
                      exalted: { bg: "from-amber-50 to-yellow-50", border: "border-amber-300", chip: "bg-amber-200 text-amber-900", icon: "text-amber-600" },
                      own: { bg: "from-emerald-50 to-teal-50", border: "border-emerald-300", chip: "bg-emerald-200 text-emerald-900", icon: "text-emerald-600" },
                      neecha_bhanga: { bg: "from-indigo-50 to-violet-50", border: "border-indigo-300", chip: "bg-indigo-200 text-indigo-900", icon: "text-indigo-600" },
                      parivartana: { bg: "from-fuchsia-50 to-rose-50", border: "border-fuchsia-300", chip: "bg-fuchsia-200 text-fuchsia-900", icon: "text-fuchsia-600" },
                    };
                    const t = tintMap[pp.dignity] || tintMap.exalted;
                    const chipLabel = {
                      exalted: "EXALTED · UCHCHA",
                      own: "OWN SIGN · SWAKSHETRA",
                      neecha_bhanga: "NEECHA BHANGA · DEBILITY CANCELLED",
                      parivartana: "PARIVARTANA · MUTUAL EXCHANGE",
                    }[pp.dignity];
                    return (
                      <div key={i} className={`rounded-2xl p-5 sm:p-6 bg-gradient-to-br ${t.bg} border ${t.border}`} data-testid={`power-placement-${i}`}>
                        <div className="flex items-center gap-2 flex-wrap">
                          <Crown size={16} weight="duotone" className={t.icon} />
                          <span className={`text-[10px] font-bold tracking-wider px-2 py-0.5 rounded-full ${t.chip}`}>{chipLabel}</span>
                        </div>
                        <h4 className="font-serif-display text-xl sm:text-2xl mt-3 font-light leading-snug text-[#3F2D1B]">{pp.headline}</h4>
                        <p className="text-sm mt-3 font-light leading-snug text-[#3F2D1B]">{pp.detail}</p>
                        {pp.dignity_note && pp.dignity_note !== pp.detail && (
                          <p className="text-xs mt-2 italic text-muted leading-snug">↳ {pp.dignity_note}</p>
                        )}
                      </div>
                    );
                  })}
                </div>
              </section>
            )}

            {/* === PARIVARTANA PAIRS === */}
            {data.parivartana && data.parivartana.length > 0 && (
              <section className="space-y-3" data-testid="parivartana-block">
                <div className="label-eyebrow text-muted">Parivartana exchanges in your D27</div>
                {data.parivartana.map((pv, i) => (
                  <div key={i} className="rounded-2xl p-5 bg-gradient-to-br from-fuchsia-50 to-rose-50 border border-fuchsia-200" data-testid={`parivartana-${i}`}>
                    <div className="font-serif-display text-xl text-fuchsia-900">
                      {pv.planets[0]} (H{pv.houses[0]} · {pv.rashis[0]}) ⇄ {pv.planets[1]} (H{pv.houses[1]} · {pv.rashis[1]})
                    </div>
                    <p className="text-sm mt-2 font-light leading-snug text-fuchsia-950">{pv.meaning}</p>
                  </div>
                ))}
              </section>
            )}

            {/* === WATCH PLACEMENTS (debilitated without bhanga) === */}
            {data.weak_placements && data.weak_placements.length > 0 && (
              <section className="space-y-3" data-testid="weak-placements">
                <div className="label-eyebrow text-rose-900">Watch placements · debility without bhanga</div>
                <p className="text-xs text-muted -mt-2 font-light max-w-2xl">
                  These planets are debilitated in your D27 with no cancellation — they ask for humility, remedy, and patient course-correction in their respective realms.
                </p>
                <div className="grid grid-cols-1 lg:grid-cols-2 gap-3">
                  {data.weak_placements.map((wp, i) => (
                    <div key={i} className="rounded-xl p-4 bg-rose-50 border border-rose-200" data-testid={`weak-placement-${i}`}>
                      <div className="text-sm font-medium text-rose-900">{wp.planet} · H{wp.house} · {wp.rashi}</div>
                      <p className="text-xs mt-2 font-light leading-snug text-rose-950">{wp.dignity_note}</p>
                    </div>
                  ))}
                </div>
              </section>
            )}

            {/* === KEY HOUSES (1, 2, 10, 11) === */}
            <section className="space-y-5">
              <div className="label-eyebrow text-muted">Wealth & career houses · 1 · 2 · 10 · 11 of D27</div>
              <div className="grid grid-cols-1 lg:grid-cols-2 gap-5">
                {data.house_readings.map((h) => {
                  const Icon = HOUSE_ICON[h.house] || House;
                  const tint = HOUSE_TINT[h.house] || "amber";
                  return (
                    <div key={h.house} className={`rounded-2xl p-5 sm:p-6 bg-${tint}-50 border border-${tint}-200`} data-testid={`house-${h.house}`}>
                      <div className={`flex items-center gap-2 label-eyebrow text-${tint}-900`}>
                        <Icon size={16} weight="duotone" /> H{h.house} · {h.label}
                      </div>
                      <div className="text-xs text-muted mt-1.5 font-medium">{h.rashi} · ruled by {h.rashi_lord}</div>
                      <p className="text-sm mt-3 font-light leading-snug">{h.desc}</p>
                      {h.planets.length > 0 ? (
                        <div className="mt-4 space-y-3 pt-3 border-t border-soft">
                          {h.planets.map((p, i) => {
                            const dignityChip = {
                              exalted: { label: "EXALTED", cls: "bg-amber-200 text-amber-900" },
                              own: { label: "OWN SIGN", cls: "bg-emerald-200 text-emerald-900" },
                              neecha_bhanga: { label: "NEECHA BHANGA", cls: "bg-indigo-200 text-indigo-900" },
                              parivartana: { label: "PARIVARTANA", cls: "bg-fuchsia-200 text-fuchsia-900" },
                              debilitated: { label: "DEBILITATED", cls: "bg-rose-200 text-rose-900" },
                            }[p.dignity];
                            return (
                              <div key={i} data-testid={`house-${h.house}-planet-${i}`}>
                                <div className="flex items-baseline gap-2 text-sm flex-wrap">
                                  <b>{p.planet}</b>
                                  {p.retrograde && <span className="text-[10px] px-1.5 py-0.5 rounded bg-rose-100 text-rose-800">R</span>}
                                  {dignityChip && (
                                    <span className={`text-[9px] font-bold tracking-wider px-1.5 py-0.5 rounded ${dignityChip.cls}`} data-testid={`dignity-chip-${h.house}-${i}`}>
                                      {dignityChip.label}
                                    </span>
                                  )}
                                  <span className="text-xs text-muted">{p.nakshatra}{p.deity ? ` · ${p.deity}` : ""}</span>
                                </div>
                                {p.dignity_house_line && (
                                  <p className="text-xs mt-1.5 font-medium leading-snug text-[#7C2D12]">★ {p.dignity_house_line}</p>
                                )}
                                <p className="text-xs text-muted mt-1 font-light leading-snug">{p.interpretation}</p>
                                {p.dignity_note && (
                                  <p className="text-[11px] mt-1.5 italic text-[#3F2D1B] leading-snug">↳ {p.dignity_note}</p>
                                )}
                                {p.deity_note && (
                                  <p className="text-[11px] mt-1.5 italic text-[#7C2D12] leading-snug">↳ {p.deity_note}</p>
                                )}
                              </div>
                            );
                          })}
                        </div>
                      ) : (
                        <p className="text-xs text-muted mt-3 italic">{h.empty_note}</p>
                      )}
                    </div>
                  );
                })}
              </div>
            </section>

            {/* === CONJUNCTION YOGAS === */}
            {data.conjunctions.length > 0 && (
              <section className="space-y-4">
                <div className="label-eyebrow text-muted">Conjunction yogas in your D27 · rare combinations</div>
                <div className="space-y-3" data-testid="conjunctions-block">
                  {data.conjunctions.map((c, i) => (
                    <div key={i} className="rounded-2xl p-5 bg-gradient-to-br from-violet-50 to-purple-50 border border-violet-200" data-testid={`conjunction-${i}`}>
                      <div className="flex items-center gap-2">
                        <span className="font-serif-display text-2xl font-light text-violet-900">
                          {c.planets.join(" + ")}
                        </span>
                        <span className="text-xs text-muted">in {c.rashi}</span>
                      </div>
                      <p className="text-sm mt-2 font-light leading-snug text-violet-950">{c.meaning}</p>
                    </div>
                  ))}
                </div>
              </section>
            )}

            {/* === FULL D27 PLANET TABLE === */}
            <section className="space-y-4">
              <div className="label-eyebrow text-muted">All 9 planets in your D27</div>
              <div className="rounded-2xl overflow-hidden border border-soft">
                <table className="w-full text-sm">
                  <thead className="bg-[#FAF4E8] text-xs text-muted">
                    <tr>
                      <th className="text-left p-3">Planet</th>
                      <th className="text-left p-3">D27 sign</th>
                      <th className="text-left p-3">House</th>
                      <th className="text-left p-3">Dignity</th>
                      <th className="text-left p-3 hidden sm:table-cell">Nakshatra</th>
                      <th className="text-left p-3 hidden md:table-cell">From D1</th>
                    </tr>
                  </thead>
                  <tbody>
                    {Object.entries(data.d27_planets).map(([name, p]) => {
                      const chip = {
                        exalted: { label: "Exalted", cls: "bg-amber-100 text-amber-900" },
                        own: { label: "Own sign", cls: "bg-emerald-100 text-emerald-900" },
                        neecha_bhanga: { label: "Neecha Bhanga", cls: "bg-indigo-100 text-indigo-900" },
                        parivartana: { label: "Parivartana", cls: "bg-fuchsia-100 text-fuchsia-900" },
                        debilitated: { label: "Debilitated", cls: "bg-rose-100 text-rose-900" },
                        neutral: { label: "—", cls: "text-muted" },
                      }[p.dignity || "neutral"];
                      return (
                        <tr key={name} className="border-t border-soft" data-testid={`d27-planet-${name}`}>
                          <td className="p-3 font-medium">{name}{p.retrograde && <span className="ml-1.5 text-rose-700 text-xs">(R)</span>}</td>
                          <td className="p-3">{p.rashi}</td>
                          <td className="p-3">H{p.house}</td>
                          <td className="p-3">
                            <span className={`text-[10px] font-bold tracking-wider px-1.5 py-0.5 rounded ${chip.cls}`}>{chip.label}</span>
                          </td>
                          <td className="p-3 text-muted hidden sm:table-cell">{p.nakshatra}</td>
                          <td className="p-3 text-muted hidden md:table-cell">{p.d1_rashi} → {p.rashi}</td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            </section>

            {/* CTA */}
            <ToolAstrologerCta
              requiredTechnique="Divisional Charts (D1–D10)"
              pitch={`Decode your full Bhamsha karma — D27 + dasha overlay — with a 16-Varga specialist`}
              ctaLabel="Talk to"
            />
          </div>
        )}
      </div>
    </PageShell>
  );
}
